import * as React from 'react';
import { JsonFormsDispatch } from '@jsonforms/react';
import {
  Generate,
  JsonFormsCellRendererRegistryEntry,
  JsonFormsRendererRegistryEntry,
} from '@jsonforms/core';
import {
  TextAction,
  HeadingText,
  VisuallyHidden,
} from '@leagueplatform/genesis-core';
import type { AnnotatedJsonSchema } from '@web-config-app/core';
import {
  ComplexObjectArrayControlItem,
  EntityFormControlAnnotationProps,
} from '@web-config-app/entity-form';
import { DrawerDialog } from '@web-config-app/core-react-ui';

export interface FlowNodeArrayItemDrawerProps {
  itemLabel: string;
  schema: AnnotatedJsonSchema;
  rootSchema: AnnotatedJsonSchema;
  path: string;
  index: number;
  enabled: boolean;
  renderers: JsonFormsRendererRegistryEntry[] | undefined;
  cells: JsonFormsCellRendererRegistryEntry[] | undefined;
  combinatorProperties?: EntityFormControlAnnotationProps['combinatorProperties'];
  data: ComplexObjectArrayControlItem[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  activeArrayItemIndex?: number | null;
}

export const FlowNodeArrayItemDrawer: React.FC<
  FlowNodeArrayItemDrawerProps
> = ({
  itemLabel,
  schema,
  rootSchema,
  path,
  index,
  enabled,
  renderers,
  cells,
  combinatorProperties,
  data,
  isOpen,
  onOpenChange,
  activeArrayItemIndex,
}) => {
  const returnFocusRef = React.useRef<HTMLButtonElement>(null);

  const childPath = React.useMemo(() => `${path}.${index}`, [path, index]);

  const drawerId = React.useMemo(() => `drawer-dialog-${index}`, [index]);

  // Calculate the correct item schema based on combinator logic
  const itemSchema = React.useMemo(() => {
    const item = data?.[index];
    if (!item) return schema.items as AnnotatedJsonSchema;

    const { combinatorDiscriminator, combinatorSubSchemaMap } =
      combinatorProperties ?? {};
    const resolvedSchema = combinatorDiscriminator
      ? combinatorSubSchemaMap?.[item.itemData[combinatorDiscriminator]]
      : schema.items;

    return resolvedSchema as AnnotatedJsonSchema;
  }, [data, index, schema.items, combinatorProperties]);

  const itemUiSchema = React.useMemo(
    () =>
      Generate.uiSchema(itemSchema, 'FlowNodeArrayItem', undefined, rootSchema),
    [itemSchema, rootSchema],
  );

  const handleOpenDrawer = React.useCallback(() => {
    returnFocusRef.current?.blur();
    onOpenChange(true);
  }, [onOpenChange]);

  return (
    <>
      <TextAction
        as="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={drawerId}
        css={{ textAlign: 'left' }}
        onClick={handleOpenDrawer}
        ref={returnFocusRef}
      >
        {itemLabel}
      </TextAction>

      <DrawerDialog
        open={isOpen}
        onClose={() => onOpenChange(false)}
        id={drawerId}
        returnFocusRef={returnFocusRef}
        returnFocusOnClose={activeArrayItemIndex === index}
      >
        <VisuallyHidden>
          <HeadingText level="2" size="xxl">
            {itemLabel}
          </HeadingText>
        </VisuallyHidden>
        <JsonFormsDispatch
          enabled={enabled}
          schema={itemSchema}
          uischema={itemUiSchema}
          path={childPath}
          key={childPath}
          renderers={renderers}
          cells={cells}
        />
      </DrawerDialog>
    </>
  );
};
